<template>
  <div class="custom-input bedrooms numeric" :class="displayError ? 'custom-error':''">
    <fa v-if="faClass" :fa-class="faClass" />

    <div class="input-inner">
      <div v-if="label" :class="getLabelClass">
        {{ $t(label) }}
      </div>

      <div class="v-align padBottom-Xsmall">
        <label v-if="isOnlineRes || isDuplicate" class="repartition">
          {{ truncate(this.$t('_rooms.one') ) }}
        </label>

        <label v-else class="repartition">
          {{ $t('_rooms.one') }}
        </label>

        <numeric-input v-if="!isSeminar" v-model="value.single.amount" name="single"
                       :min="0" :max="max.single" align="center" size="4rem"
                       @input="onInputSingle" ref="num-input-single"
        />

        <numeric-input v-else v-model="value.single" name="single" :min="0"
                       align="center" size="4rem" @input="onInputSingle"
        />

        <span v-if="isHome" id="bedrooms-repartition-info">
          <i class="fas fa-question"></i>
        </span>

        <b-tooltip target="bedrooms-repartition-info">
          {{$t('home.form.bedrooms.tooltip_message')}}
        </b-tooltip>
      </div>

      <div v-if="!onlyOne" class="v-align padBottom-Xsmall">
        <label v-if="isOnlineRes || isDuplicate" class="repartition" for="single">
          {{ truncate(this.$t('_rooms.two') ) }}
        </label>

        <label v-else class="repartition" for="double">
          {{ $t('_rooms.two') }}
        </label>

        <numeric-input v-if="!isSeminar" v-model="value.double.amount" name="double"
                       :min="0" :max="max.double" align="center" size="4rem"
                       @input="onInputDouble" ref="num-input-double"
        />

        <numeric-input v-else v-model="value.double" name="double" :min="0"
                        align="center" size="4rem" @input="onInputDouble"
        />
      </div>
    </div>
    <error v-if="displayError">
      {{ errorMessage }}
    </error>
  </div>
</template>

<script>
import fa from '@/components/global/icon/fontAwesome'
import numericInput from 'vue-numeric-input'
import formMethods from '@/components/frontoffice/mixins/formMethods'
import error from '@/components/frontoffice/form/error'
import CommonApi from '@/api/commonApi'

export default {
  components: {
    fa,
    numericInput,
    error
  },

  mixins: [formMethods],

  props: {
    label: {
      default: ''
    },
    faClass: {
      default: 'bed'
    },
    value: {
      type: Object,
      default () {
        return {
          single: {
            amount: 0,
            travellers: []
          },
          double: {
            amount: 0,
            travellers: []
          }
        }
      }
    },
    required: {
      type: Boolean,
      default: true
    },
    isUnlogged: {
      type: Boolean,
      default: true
    },
    companyOnlyOne: {
      default: null
    }
  },

  created () {
    if (this.isUnlogged) {
      this.onlyOne = this.$store.getters['security/company'].onlyOne
    } else {
      this.onlyOne = this.companyOnlyOne
    }

    if (this.onlyOne) {
      this.max.single = 1
      this.max.double = 0
    } else {
      this.max.travellers = this.commonApi.bookingMaxTravellers()
      this.max.single = this.max.travellers
      this.max.double = this.max.travellers
    }

    if (this.currentPage === 'seminar') {
      this.isSeminar = true
    }
    if (this.currentPage === 'home_search') {
      this.isHome = true
    }
    if (this.currentPage === 'online_results') {
      this.isOnlineRes = true
    }
    if (this.currentPage === 'offline_home') {
      this.isOffline = true
    }
    if (this.currentPage === 'home_booking_duplicate') {
      this.isDuplicate = true
    }
    this.last.single = this.value.single.amount
    this.last.double = this.value.double.amount
    this.errorMessage = this.$t('home.form.bedrooms.maxRooms', { max: this.max.travellers })
  },

  data () {
    return {
      currentPage: this.$route.name,
      isHome: false,
      isSeminar: false,
      isOnlineRes: false,
      isOffline:false,
      isDuplicate: false,
      displayError: false,
      errorMessage: '',
      onlyOne: false,
      max: {
        single: 0,
        double: 0,
        travellers: 0
      },
      commonApi: new CommonApi(),
      last: {
        single: null,
        double: null
      }
    }
  },

  methods: {
    onInputSingle (newVal) {
      let oldVal = this.last.single

      if (this.onlyOne) {
        this.max.double = newVal === 0 ? 1 : 0
      } else {
        this.max.double = Math.max(this.max.travellers - newVal, 0)
      }

      this.last.single = newVal

      this.$emit('single', newVal > oldVal ? 'add' : 'minus')
    },

    onInputDouble (newVal) {
      let oldVal = this.last.double

      if (this.onlyOne) {
        this.max.single = newVal === 0 ? 1 : 0
      } else {
        this.max.single = Math.max(this.max.travellers - newVal, 0)
      }

      this.last.double = newVal

      this.$emit('double', newVal > oldVal ? 'add' : 'minus')
    },

    truncate (val) {
      let length =
        this.isViewPortMobileMedium ? 11 :
        this.isViewPortMobileLarge ? 16 :
        21

      return this.$options.filters.truncate(val, length)
    }
  },

  computed: {
    canIncrement () {
      let single = this.value.single.amount
      let double = this.value.double.amount
      return (single + double) < this.max.travellers
    }
  },

  watch: {
    canIncrement (can) {
      // Create a clone of increment button when it is disabled
      // so as to listen to click event and display error message

      let refs = Object.entries(this.$refs).filter(ref => {
        return ref[0].match(/^num-input/)
      }).map(ref => ref[1])

      setTimeout( () => { // need to wait a bit otherwise DOM is not updated
        for (let ref of refs) {
          let nodes = []

          ref.$el.childNodes.forEach( node =>{
            if (node.classList && node.classList.contains('btn-increment'))
              nodes.push(node)
          })

          if (can) {
            nodes.forEach( node => {
              if (node.classList.contains('clone-increment')) {
                ref.$el.removeChild(node)
              }
            })

            this.displayError = false
          } else {
            let clone = null

            nodes.forEach( node => {
              if (node.disabled) {
                clone = node.cloneNode(true)
                clone.disabled = false
                clone.classList.add('clone-increment')
                clone.addEventListener('click', () => {
                  this.displayError = true
                })
              }
            })

            if (clone !== null) ref.$el.append(clone)
          }
        }
      }, 0.1)
    }
  }
}
</script>
